import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, Copy, Loader, Mail, MessageSquare, Save, UserCheck, X } from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';

const DEFAULT_SUBJECT_TEMPLATE = 'Handoff for {customerFirstName} {customerLastName} ({storeName}) ({vehicleMake} {vehicleModel})';

const DEFAULT_BODY_TEMPLATE = `Hello Team,

{customerFirstName} {customerLastName} has requested assistance from the dealership. The customer's question requires dealership support, and the conversation transcript is attached for your reference.

Customer Details:
Name: {customerFirstName} {customerLastName}
Phone: {customerPhone}
Email: {customerEmail}

Would someone from the team please connect with the customer at the earliest convenience?

Thank you,
{assistantFirstName} {assistantLastName}`;

const DEFAULT_SMS_TEMPLATE = 'Let me talk to one of my colleagues to get that answered for you. Let me go ahead and do that.';

const DEFAULT_ASD_SUBJECT_TEMPLATE = 'ASD escalation: {customerFirstName} {customerLastName} ({storeName})';

const DEFAULT_ASD_BODY_TEMPLATE = `Hello,

A conversation at {storeName} has been escalated to you for visibility.

Customer Details:
Name: {customerFirstName} {customerLastName}
Phone: {customerPhone}
Email: {customerEmail}
Vehicle: {vehicleMake} {vehicleModel}

Reason for escalation:
{reason}

The full conversation transcript is attached.

Thank you,
{assistantFirstName} {assistantLastName}`;

const HANDOFF_PLACEHOLDERS = [
  'customerFirstName',
  'customerLastName',
  'customerPhone',
  'customerEmail',
  'vehicleMake',
  'vehicleModel',
  'storeName',
  'assistantFirstName',
  'assistantLastName',
  'reason'
];

const HandoffConfigManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [copiedPlaceholder, setCopiedPlaceholder] = useState(null);

  const [formData, setFormData] = useState({
    emailSubjectTemplate: '',
    emailBodyTemplate: '',
    smsTemplate: '',
    asdEmailSubjectTemplate: '',
    asdEmailBodyTemplate: ''
  });

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadConfig(selectedStore.id);
    }
  }, [selectedStore]);

  const loadStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setStores(response.data);
      if (response.data.length > 0) {
        setSelectedStore(response.data[0]);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async (storeId) => {
    setError('');
    setSuccess('');
    setConfig(null);
    setShowForm(false);
    setError('');
    try {
      const response = await api.get('/admin/handoff-config', { params: { storeIdFk: storeId } });
      setConfig(response.data);
      setFormData({
        emailSubjectTemplate: response.data.emailSubjectTemplate || DEFAULT_SUBJECT_TEMPLATE,
        emailBodyTemplate: response.data.emailBodyTemplate || DEFAULT_BODY_TEMPLATE,
        smsTemplate: response.data.smsTemplate || DEFAULT_SMS_TEMPLATE,
        asdEmailSubjectTemplate: response.data.asdEmailSubjectTemplate || DEFAULT_ASD_SUBJECT_TEMPLATE,
        asdEmailBodyTemplate: response.data.asdEmailBodyTemplate || DEFAULT_ASD_BODY_TEMPLATE
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setFormData({
          emailSubjectTemplate: DEFAULT_SUBJECT_TEMPLATE,
          emailBodyTemplate: DEFAULT_BODY_TEMPLATE,
          smsTemplate: DEFAULT_SMS_TEMPLATE,
          asdEmailSubjectTemplate: DEFAULT_ASD_SUBJECT_TEMPLATE,
          asdEmailBodyTemplate: DEFAULT_ASD_BODY_TEMPLATE
        });
      } else {
        console.error('Error loading handoff config:', err);
        setError('Failed to load handoff configuration');
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedStore) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        storeIdFk: selectedStore.id
      };
      const response = await api.post('/admin/handoff-config', payload);
      setConfig(response.data);
      setSuccess('Handoff configuration saved successfully');
      setShowForm(false);
    } catch (err) {
      console.error('Error saving handoff config:', err);
      const message = err.response?.data?.error || 'Failed to save handoff configuration';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setFormData({
      emailSubjectTemplate: config?.emailSubjectTemplate || DEFAULT_SUBJECT_TEMPLATE,
      emailBodyTemplate: config?.emailBodyTemplate || DEFAULT_BODY_TEMPLATE,
      smsTemplate: config?.smsTemplate || DEFAULT_SMS_TEMPLATE,
      asdEmailSubjectTemplate: config?.asdEmailSubjectTemplate || DEFAULT_ASD_SUBJECT_TEMPLATE,
      asdEmailBodyTemplate: config?.asdEmailBodyTemplate || DEFAULT_ASD_BODY_TEMPLATE
    });
    setShowForm(true);
    setError('');
  };

  const handleTemplateReset = (field, defaultValue) => {
    setFormData((prev) => ({ ...prev, [field]: defaultValue }));
  };

  const copyPlaceholder = (placeholder) => {
    const text = `{${placeholder}}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedPlaceholder(placeholder);
          setTimeout(() => setCopiedPlaceholder(null), 1500);
        })
        .catch((err) => console.error('Failed to copy placeholder:', err));
    }
  };

  if (loading) return <AdminLoading message="Loading handoff configuration..." />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={UserCheck}
        title="Handoff Configuration"
        subtitle="Configure email templates and the customer SMS sent on handoff"
        rightContent={
          <select
            value={selectedStore?.id || ''}
            onChange={(e) => {
              const store = stores.find((s) => s.id === parseInt(e.target.value));
              setSelectedStore(store);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name || store.storeId}
              </option>
            ))}
          </select>
        }
      />

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 rounded-md p-4">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-medium text-blue-900">Available placeholders</h5>
          <span className="text-xs text-blue-700">Click to copy</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {HANDOFF_PLACEHOLDERS.map((placeholder) => (
            <button
              key={placeholder}
              type="button"
              onClick={() => copyPlaceholder(placeholder)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-800 bg-white border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={`Copy {${placeholder}}`}
            >
              <code className="font-mono">{`{${placeholder}}`}</code>
              {copiedPlaceholder === placeholder ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3 text-blue-400" />
              )}
            </button>
          ))}
        </div>
      </div>

      {!showForm && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Current Handoff Templates</h3>
              <p className="text-sm text-gray-500">Store: {selectedStore?.name || selectedStore?.storeId}</p>
            </div>
            <button
              onClick={startEditing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Edit Templates
            </button>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-blue-500 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-50 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Email Template</h3>
                <p className="text-sm text-gray-500">Sent to the dealership team on handoff</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Subject</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                  {config?.emailSubjectTemplate || DEFAULT_SUBJECT_TEMPLATE}
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Body</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap min-h-[100px]">
                  {config?.emailBodyTemplate || DEFAULT_BODY_TEMPLATE}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-purple-500 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-50 rounded-full">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">ASD Email Template</h3>
                <p className="text-sm text-gray-500">
                  Sent to directors and managers, only when a manual handoff ticks "Send to ASD"
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Subject</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                  {config?.asdEmailSubjectTemplate || DEFAULT_ASD_SUBJECT_TEMPLATE}
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Body</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap min-h-[100px]">
                  {config?.asdEmailBodyTemplate || DEFAULT_ASD_BODY_TEMPLATE}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-green-500 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-50 rounded-full">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Customer SMS</h3>
                <p className="text-sm text-gray-500">Reply sent to the customer on handoff</p>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</h4>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                {config?.smsTemplate || DEFAULT_SMS_TEMPLATE}
              </p>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {config?.id ? 'Edit Handoff Templates' : 'Create Handoff Templates'}
            </h3>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-blue-500 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-blue-50 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Email Template</h3>
                <p className="text-sm text-gray-500">Sent to the dealership team on handoff</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <button
                    type="button"
                    onClick={() => handleTemplateReset('emailSubjectTemplate', DEFAULT_SUBJECT_TEMPLATE)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Reset to default
                  </button>
                </div>
                <input
                  type="text"
                  value={formData.emailSubjectTemplate}
                  onChange={(e) => setFormData({ ...formData, emailSubjectTemplate: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Body</label>
                  <button
                    type="button"
                    onClick={() => handleTemplateReset('emailBodyTemplate', DEFAULT_BODY_TEMPLATE)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={formData.emailBodyTemplate}
                  onChange={(e) => setFormData({ ...formData, emailBodyTemplate: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  rows={10}
                  required
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-purple-500 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-purple-50 rounded-full">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">ASD Email Template</h3>
                <p className="text-sm text-gray-500">
                  Sent to directors and managers, only when a manual handoff ticks "Send to ASD"
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <button
                    type="button"
                    onClick={() => handleTemplateReset('asdEmailSubjectTemplate', DEFAULT_ASD_SUBJECT_TEMPLATE)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Reset to default
                  </button>
                </div>
                <input
                  type="text"
                  value={formData.asdEmailSubjectTemplate}
                  onChange={(e) => setFormData({ ...formData, asdEmailSubjectTemplate: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Body</label>
                  <button
                    type="button"
                    onClick={() => handleTemplateReset('asdEmailBodyTemplate', DEFAULT_ASD_BODY_TEMPLATE)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={formData.asdEmailBodyTemplate}
                  onChange={(e) => setFormData({ ...formData, asdEmailBodyTemplate: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  rows={10}
                  required
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-green-500 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-green-50 rounded-full">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Customer SMS</h3>
                <p className="text-sm text-gray-500">Reply sent to the customer on handoff</p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <button
                  type="button"
                  onClick={() => handleTemplateReset('smsTemplate', DEFAULT_SMS_TEMPLATE)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Reset to default
                </button>
              </div>
              <textarea
                value={formData.smsTemplate}
                onChange={(e) => setFormData({ ...formData, smsTemplate: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                rows={3}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                This message is sent to the customer when a manual handoff is triggered.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 flex items-center"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default HandoffConfigManagement;
